// Merchant relationship Status (FIG-316 / REQ-004).
//
// Since FIG-345 (UC-226) organisations.status stores one of these six values
// and only admins change it; onboarding progress moved to
// organisations.onboarding_state (UC-225). The legacy mapping below is only a
// fallback for rows not yet migrated (e.g. during the deploy window).

export type MerchantStatusId =
  | 'onboarding'
  | 'pending'
  | 'contracted'
  | 'watching'
  | 'archived'
  | 'test';

export type MerchantStatusGroup = 'Pipeline' | 'Not in pipeline' | 'Other';

export interface MerchantStatusDefinition {
  id: MerchantStatusId;
  label: string;
  group: MerchantStatusGroup;
  meaning: string;
  /** False while the backend cannot store this status. All six are stored since FIG-345 (UC-226). */
  available: boolean;
  /** Tailwind classes for the dot-plus-label badge. */
  badgeClassName: string;
  dotClassName: string;
}

/** Display order everywhere: Onboarding, Pending, Contracted, Watching, Archived, Test. */
export const MERCHANT_STATUSES: readonly MerchantStatusDefinition[] = [
  {
    id: 'onboarding',
    label: 'Onboarding',
    group: 'Pipeline',
    meaning: 'New merchant. Every new record starts here.',
    available: true,
    badgeClassName: 'bg-slate-100 border-slate-300 text-slate-800',
    dotClassName: 'bg-slate-500',
  },
  {
    id: 'pending',
    label: 'Pending',
    group: 'Pipeline',
    meaning: 'Onboarding information submitted, under review.',
    available: true,
    badgeClassName: 'bg-amber-50 border-amber-300 text-amber-900',
    dotClassName: 'bg-amber-600',
  },
  {
    id: 'contracted',
    label: 'Contracted',
    group: 'Pipeline',
    meaning: 'Purchase program agreement in place.',
    available: true,
    badgeClassName: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    dotClassName: 'bg-indigo-500',
  },
  {
    id: 'watching',
    label: 'Watching',
    group: 'Not in pipeline',
    meaning: 'Not progressing to a contract for now, for example sharing data and ready to sign later.',
    available: true,
    badgeClassName: 'bg-purple-50 border-purple-200 text-purple-900',
    dotClassName: 'bg-purple-600',
  },
  {
    id: 'archived',
    label: 'Archived',
    group: 'Not in pipeline',
    meaning: 'No longer being pursued.',
    available: true,
    badgeClassName: 'bg-gray-200 border-gray-300 text-gray-800',
    dotClassName: 'bg-gray-700',
  },
  {
    id: 'test',
    label: 'Test',
    group: 'Other',
    meaning: 'Test or internal record.',
    available: true,
    badgeClassName: 'bg-white border-dashed border-slate-400 text-slate-800',
    dotClassName: 'bg-slate-500',
  },
];

export const MERCHANT_STATUS_GROUPS: readonly MerchantStatusGroup[] = ['Pipeline', 'Not in pipeline', 'Other'];

/** The default Merchants list view shows the pipeline only (REQ-011). */
export const PIPELINE_STATUS_IDS: readonly MerchantStatusId[] = MERCHANT_STATUSES
  .filter((s) => s.group === 'Pipeline')
  .map((s) => s.id);

export const ALL_STATUS_IDS: readonly MerchantStatusId[] = MERCHANT_STATUSES.map((s) => s.id);

const STATUS_BY_ID = new Map(MERCHANT_STATUSES.map((s) => [s.id, s]));

export function getMerchantStatus(id: MerchantStatusId): MerchantStatusDefinition {
  // Every MerchantStatusId is in the table, so this lookup cannot miss.
  return STATUS_BY_ID.get(id) as MerchantStatusDefinition;
}

export function isMerchantStatusId(value: unknown): value is MerchantStatusId {
  return typeof value === 'string' && STATUS_BY_ID.has(value as MerchantStatusId);
}

/** Label on the Status filter pill: All, Pipeline, None, one or two names, or "n of 6". */
export function getStatusFilterLabel(selected: ReadonlySet<MerchantStatusId>): string {
  const checked = MERCHANT_STATUSES.filter((s) => selected.has(s.id));
  if (checked.length === MERCHANT_STATUSES.length) return 'All';
  const isPipelineView = MERCHANT_STATUSES.every((s) => selected.has(s.id) === PIPELINE_STATUS_IDS.includes(s.id));
  if (isPipelineView) return 'Pipeline';
  if (checked.length === 0) return 'None';
  if (checked.length <= 2) return checked.map((s) => s.label).join(', ');
  return `${checked.length} of ${MERCHANT_STATUSES.length}`;
}

export const TEST_NAME_PREFIX = '[TEST]';

export function hasTestNamePrefix(name: string | null | undefined): boolean {
  return (name ?? '').trimStart().startsWith(TEST_NAME_PREFIX);
}

/** Name without the [TEST] prefix, for sorting and initials. */
export function stripTestNamePrefix(name: string | null | undefined): string {
  const value = (name ?? '').trimStart();
  return hasTestNamePrefix(value) ? value.slice(TEST_NAME_PREFIX.length).trimStart() : value;
}

export interface MerchantStatusSource {
  name: string | null | undefined;
  /** organisations.status: the relationship Status (UC-226). */
  status?: string | null;
  is_test_clone?: boolean | null;
}

/**
 * The merchant's Status. Reads the stored value; a legacy onboarding value
 * ('draft' / 'submitted' / null, before the FIG-345 migration) falls back to
 * the mapping the migration applies: [TEST] or test clone -> Test,
 * draft -> Onboarding, anything else -> Pending.
 */
export function deriveMerchantStatus(org: MerchantStatusSource): MerchantStatusId {
  if (isMerchantStatusId(org.status)) return org.status;
  if (org.is_test_clone || hasTestNamePrefix(org.name)) return 'test';
  if (org.status === 'draft') return 'onboarding';
  return 'pending';
}
